<template>
    <div class="generator-prompt-input-container">
        <div v-if="editor" class="input-text-box" :class="[{focused:focused, error:error}]">
            <EditorContent :editor="editor"
                           :class="'ufo--text-area-generator-prompt'"
            />
            <div class="editor-toolbar">
                <div class="buttons">
                    <SimpleAiButton v-if="!prompt.length" @click="useDraft" icon="add-text" :text="draftButtonText ?? 'Add starter prompt'" />
                </div>
                <div class="char-counter" v-if="editor && internalMaxLength">
                    {{ editor.getText().length }} / {{ internalMaxLength }}
                </div>
            </div>
        </div>
        <div class="error-message" v-if="error"> {{ errorMessage }} </div>
    </div>
</template>

<script>
    import {Editor, EditorContent} from "@tiptap/vue-3";
    import StarterKit from "@tiptap/starter-kit";
    import Placeholder from "@tiptap/extension-placeholder";
    import SimpleAiButton from "@/scripts/common/components/SimpleAiButton.vue";
    import {CustomPlaceholder} from "@/scripts/common/TiptapCustomPlaceholder.js";
    import MixpanelService from "../../../../analytics/mixpanel.js";

    export default {
        name: "PromptSelector",
        emits: ["useDraftClicked"],
        props:[
            'prompt',
            'setPrompt',
            'draft',
            'placeholder',
            'error',
            'errorMessage',
            'setPromptHtml',
            'promptHtml',
            'draftButtonText',
            'maxLength'
        ],
        components: {
            SimpleAiButton,
            EditorContent
        },
        data() {
            return {
                editor:null,
                focused:false,
                internalMaxLength: this.maxLength || null
            }
        },
        mounted() {
            this.editor = new Editor({
                content: '',
                extensions: [
                    StarterKit,
                    CustomPlaceholder,
                    Placeholder.configure({
                        placeholder: this.placeholder,
                        showOnlyWhenEditable: true,
                        showOnlyCurrent: true
                    }),
                ],
                onUpdate: () => {
                    const plainText = this.editor.getText();

                    // If maxLength is not set, allow unrestricted input
                    if (this.internalMaxLength && plainText.length > this.internalMaxLength) {
                        // Trim text to allowed length
                        const trimmedText = plainText.slice(0, this.internalMaxLength);
                        this.editor.commands.setContent(trimmedText, { emitUpdate: false }); // Prevent undo loop
                        return;
                    }

                    this.applyTextEdit(this.editor.getHTML(), plainText);
                },
                onBlur: () => {
                    this.focused = false;
                },
                onFocus: () => {
                    this.focused = true;
                }
            })

            if(this.promptHtml) {
                if (/<[a-z][\s\S]*>/i.test(this.promptHtml)) {
                    this.editor.commands.setContent(this.promptHtml, { emitUpdate: false });
                }
                else {
                    const nodes = this.convertTextToNodes(this.promptHtml);
                    this.editor.chain().focus().insertContent(nodes).run();
                }
            }
        },
        methods: {
            getPlainTextWithPlaceholders() {
                const json = this.editor.getJSON();
                return this.convertJsonToText(json);
            },
            convertJsonToText(node) {
                if (node.content && Array.isArray(node.content)) {
                    return node.content.map(child => this.convertJsonToText(child)).join('');
                }
                if (node.type === 'customPlaceholder' && node.attrs && node.attrs.value) {
                    return node.attrs.value;
                }
                if (node.text) {
                    return node.text;
                }
                return '';
            },
            useDraft() {
                const nodes = this.convertTextToNodes(this.draft);
                this.editor.chain().focus().insertContent(nodes).run();
                this.setPrompt(this.draft);
                this.$emit('useDraftClicked');

                if (this.draftButtonText === 'Use example') {
                    MixpanelService.track("USE_EXAMPLE_CTA_CLICKED", {
                        ...MixpanelService.getUrls(),
                        event_group: MixpanelService.MixpanelEventGroups.JOB_FIT_CHECKER,
                        source: "Job Description Input Page",
                    });
                }
            },
            applyTextEdit(newTextHtml, newText) {
                const textWithPlaceholders = this.getPlainTextWithPlaceholders();
                this.setPromptHtml(newText.length ? newTextHtml : '');
                this.setPrompt(textWithPlaceholders.length ? textWithPlaceholders : '');
            },
            convertTextToNodes(text) {
                const nodes = [];
                const regex = /\[([^\]]+)]/g;
                let lastIndex = 0;
                let match;

                while ((match = regex.exec(text)) !== null) {
                    // If there's any text before the placeholder, add it as a text node.
                    if (match.index > lastIndex) {
                        nodes.push({
                            type: 'text',
                            text: text.slice(lastIndex, match.index),
                        });
                    }
                    // Add the placeholder as a custom node.
                    nodes.push({
                        type: 'customPlaceholder',
                        attrs: { value: "[" + match[1] + "]" },
                    });
                    lastIndex = regex.lastIndex;
                }
                // Add any remaining text after the last placeholder.
                if (lastIndex < text.length) {
                    nodes.push({
                        type: 'text',
                        text: text.slice(lastIndex),
                    });
                }
                return nodes;
            },
            clearEditor() {
                if (this.editor) {
                    this.editor.commands.clearContent({ emitUpdate: false });
                    this.setPrompt('');
                    this.setPromptHtml('');
                }
            }
        }
    }
</script>

<style lang="scss">
    .ufo--text-area-generator-prompt {
        .ProseMirror {
            font: var(--editor-ui-b2-regular);
            color: var(--asphalt20);
            width: 100%;
            height: 100%;
            line-height: 24px;

            p {
                margin-bottom: 1px;
            }

            .custom-placeholder {
                color: var(--asphalt40);
                border-radius: 2px;
                padding: 0 2px;
                cursor: pointer;

                &:hover {
                    background-color: var(--asphalt80);
                    color: var(--asphalt10);
                    cursor: pointer;
                }

                &.ProseMirror-selectednode {
                    background-color: var(--asphalt10);
                    color:var(--asphalt100);
                }
            }
        }

        .ProseMirror-focused {
            border: none;
            outline: none;
            p.is-empty {
                &:first-child::before {
                    color: var(--asphalt60) !important;
                }
            }
        }

        p.is-empty {
            &:first-child::before {
                content: attr(data-placeholder);
                float: left;
                color: var(--asphalt40);
                pointer-events: none;
                height: 0;
            }
        }
    }
</style>

<style scoped lang="scss">
    .generator-prompt-input-container {
        height: 100%;
        width: 100%;

        .input-text-box {
            border-radius: 6px;
            border: 1px solid var(--asphalt80);
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex: 1 0 0;
            align-self: stretch;
            height: 200px;

            .char-counter {
                font: var(--editor-ui-b2-regular);
                color: var(--asphalt60);
                align-self: flex-end;
            }

            &.error {
                border: 1px solid var(--rose50);
            }

            .ufo--text-area-generator-prompt {
                display: flex;
                padding: 16px;
                flex-direction: column;
                align-items: flex-start;
                gap: 20px;
                flex: 1 0 0;
                align-self: stretch;
                overflow: auto;

                ::-webkit-scrollbar-track {
                    background-color: transparent;
                }

                scrollbar-color: #888 transparent;
            }

            &.focused {
                &:not(.error) {
                    border: 1px solid var(--asphalt60);
                }
            }

            .editor-toolbar {
                display: flex;
                padding: 10px 16px;
                justify-content: space-between;
                align-items: center;
                align-self: stretch;
            }
        }

        .error-message {
            margin: 12px 0 0 16px;
            color: var(--rose50);
            font: var(--editor-ui-b3-regular);
        }
    }
</style>